import { Box, Typography } from "@mui/material";
import { Dayjs } from "dayjs";
import { useCallback, useMemo, useRef, useState } from "react";
import { useTimelineZoom } from "../contexts/TimelineZoomContext";

interface MarkerScale {
  interval: number;
  idealDuration: number;
  format: string;
}

const MARKER_SCALES: readonly MarkerScale[] = [
  { interval: 1, idealDuration: 12, format: "LTS" }, // Show seconds
  { interval: 10, idealDuration: 60, format: "LTS" }, // Show seconds
  { interval: 60, idealDuration: 300, format: "LT" }, // Hide seconds
  { interval: 300, idealDuration: 1800, format: "LT" }, // Hide seconds
] as const;

const formatMarkerTime = (
  seconds: number,
  videoStart: Dayjs,
  format: string
): string => {
  const time = videoStart.add(seconds, "second");
  return time.format(format);
};

const getMarkerOpacity = (duration: number, scale: MarkerScale): number => {
  const ratio = duration / scale.idealDuration;
  // Fade in when duration is 0.5x to 2x the ideal duration
  if (ratio < 0.5) {
    // Too zoomed in - fade out aggressively
    const normalized = ratio / 0.5;
    return Math.max(0, normalized * normalized); // Quadratic fade
  } else if (ratio > 2) {
    // Too zoomed out - fade out very aggressively
    const normalized = Math.min(1, (ratio - 2) / 8); // Fade over 8x range
    return Math.max(0, 1 - normalized * normalized * normalized); // Cubic fade
  }
  return 1;
};

const getMarkerSize = (duration: number, scale: MarkerScale): number => {
  const ratio = duration / scale.idealDuration;
  // Scale between 0.3 and 1.0
  if (ratio < 0.5) {
    const normalized = ratio / 0.5;
    return 0.3 + 0.7 * (normalized * normalized); // Quadratic scaling
  } else if (ratio > 2) {
    const normalized = Math.min(1, (ratio - 2) / 8);
    return Math.max(0.3, 1 - 0.7 * (normalized * normalized)); // Quadratic scaling
  }
  return 1;
};

const getTextOpacity = (duration: number, scale: MarkerScale): number => {
  const ratio = duration / scale.idealDuration;
  // Text fades out much more aggressively - only visible near ideal duration
  if (ratio < 0.7) {
    // Fade out very aggressively when too zoomed in
    const normalized = ratio / 0.7;
    return Math.max(0, normalized * normalized * normalized * normalized); // Quartic fade
  } else if (ratio > 1.5) {
    // Fade out very aggressively when too zoomed out
    const normalized = Math.min(1, (ratio - 1.5) / 3); // Fade over 3x range
    return Math.max(0, 1 - normalized * normalized * normalized * normalized); // Quartic fade
  }
  return 1;
};

export const TimelineMarkers = ({
  videoStart,
  visibleStart: visibleStartOverride,
  visibleDuration,
}: {
  videoStart: Dayjs;
  visibleStart?: Dayjs;
  visibleDuration?: number;
}) => {
  const context = useTimelineZoom();
  const duration = visibleDuration ?? context?.duration;
  const visibleStart = visibleStartOverride ?? context?.visibleStart;
  const baseTime = visibleStart ?? videoStart;

  const [containerWidth, setContainerWidth] = useState(0);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const measureContainerRef = useCallback((node: HTMLDivElement | null) => {
    resizeObserverRef.current?.disconnect();
    if (node === null) return;
    setContainerWidth(node.getBoundingClientRect().width);
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width;
      if (width !== undefined) setContainerWidth(width);
    });
    observer.observe(node);
    resizeObserverRef.current = observer;
  }, []);

  const markersByScale = useMemo(() => {
    if (duration === undefined) return [];

    const allScales = MARKER_SCALES.map((scale) => {
      const markers: {
        position: number;
        label: string;
        time: number;
        opacity: number;
        textOpacity: number;
        size: number;
      }[] = [];
      const { interval } = scale;

      const opacity = getMarkerOpacity(duration, scale);
      const textOpacity = getTextOpacity(duration, scale);
      const size = getMarkerSize(duration, scale);

      // Skip if opacity is too low (marker scale not relevant)
      if (opacity < 0.1) {
        return { scale, markers: [], opacity };
      }

      // Align all intervals to clock boundaries for clean time display
      // e.g., 10s markers at :00, :10, :20, :30, etc.
      const startSeconds = baseTime.unix();
      const firstAlignedSeconds = Math.ceil(startSeconds / interval) * interval;
      const firstMarkerOffset = firstAlignedSeconds - startSeconds;

      let currentOffset = firstMarkerOffset;
      while (currentOffset <= duration) {
        if (currentOffset >= 0) {
          const position = (currentOffset / duration) * 100;
          markers.push({
            position,
            label: formatMarkerTime(currentOffset, baseTime, scale.format),
            time: currentOffset,
            opacity,
            textOpacity,
            size,
          });
        }
        currentOffset += interval;
      }

      return { scale, markers, opacity };
    }).filter((group) => group.markers.length > 0);

    const markersByTime = new Map<
      number,
      {
        scale: MarkerScale;
        marker: {
          position: number;
          label: string;
          time: number;
          opacity: number;
          textOpacity: number;
          size: number;
        };
      }
    >();

    allScales.forEach((scaleGroup) => {
      scaleGroup.markers.forEach((marker) => {
        const timeKey = Math.round(marker.time);
        const existing = markersByTime.get(timeKey);

        if (
          !existing ||
          // floating point issues, works better with a small epsilon
          marker.opacity - 0.01 > existing.marker.opacity ||
          (marker.opacity === existing.marker.opacity &&
            marker.size - 0.01 > existing.marker.size)
        ) {
          markersByTime.set(timeKey, { scale: scaleGroup.scale, marker });
        }
      });
    });

    const result = allScales.map((scaleGroup) => ({
      ...scaleGroup,
      markers: [] as typeof scaleGroup.markers,
    }));

    markersByTime.forEach(({ scale, marker }) => {
      const scaleGroup = result.find((g) => g.scale === scale);
      if (scaleGroup) {
        scaleGroup.markers.push(marker);
      }
    });

    return result.filter((group) => group.markers.length > 0);
  }, [duration, baseTime]);

  // Hide labels that would overlap a neighbour or overhang the container edge,
  // keeping the tick marks so the scale still reads as a ruler at short windows.
  const labelsToHide = useMemo(() => {
    const hidden = new Set<number>();
    if (containerWidth === 0) return hidden;

    const estimateLabelWidthPx = (label: string) => label.length * 6.5 + 10;
    const LABEL_GAP_PX = 6;
    const EDGE_MARGIN_PX = 2;

    const labelledMarkers = markersByScale
      .flatMap((group) => group.markers)
      .filter((marker) => marker.textOpacity > 0.15)
      .sort((a, b) => a.position - b.position);

    let lastShownCenterPx: number | null = null;
    let lastShownHalfPx = 0;
    for (const marker of labelledMarkers) {
      const centerPx = (marker.position / 100) * containerWidth;
      const halfPx = estimateLabelWidthPx(marker.label) / 2;

      const overhangsEdge =
        centerPx - halfPx < EDGE_MARGIN_PX ||
        centerPx + halfPx > containerWidth - EDGE_MARGIN_PX;
      const overlapsPrevious =
        lastShownCenterPx !== null &&
        centerPx - lastShownCenterPx < halfPx + lastShownHalfPx + LABEL_GAP_PX;

      if (overhangsEdge || overlapsPrevious) {
        hidden.add(Math.round(marker.time));
        continue;
      }

      lastShownCenterPx = centerPx;
      lastShownHalfPx = halfPx;
    }
    return hidden;
  }, [markersByScale, containerWidth]);

  if (duration === undefined) return null;

  return (
    <Box
      ref={measureContainerRef}
      sx={{
        position: "absolute",
        top: 20,
        left: 0,
        right: 0,
        height: 20,
        pointerEvents: "none",
      }}
    >
      {markersByScale.map(({ scale, markers }) =>
        markers.map((marker, index) => (
          <Box
            // eslint-disable-next-line @eslint-react/no-array-index-key
            key={`${scale.interval}-${marker.time}-${index}`}
            sx={{
              position: "absolute",
              left: `${marker.position}%`,
              transform: "translateX(-50%)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              transition: "opacity 0.2s ease, transform 0.2s ease",
              opacity: marker.opacity,
            }}
          >
            {/* Tick mark */}
            <Box
              sx={{
                width: "1px",
                height: 8 * marker.size,
                backgroundColor: "text.secondary",
                opacity: 0.4,
                mb: 0.5,
                transition: "height 0.2s ease",
              }}
            />
            {labelsToHide.has(Math.round(marker.time)) ? null : (
              <Typography
                variant="caption"
                sx={{
                  fontSize: `${0.65 * marker.size}rem`,
                  color: "text.secondary",
                  whiteSpace: "nowrap",
                  userSelect: "none",
                  opacity: marker.textOpacity,
                  transition: "font-size 0.2s ease, opacity 0.2s ease",
                }}
              >
                {marker.label}
              </Typography>
            )}
          </Box>
        ))
      )}
    </Box>
  );
};
